/**
 * Mesh decimation via meshoptimizer's MeshoptSimplifier.
 *
 * Runs at load time, before topology validation ("the checks"), to bring the
 * vertex / triangle count down. meshopt's simplify() only rewrites the index
 * buffer (leaving vertices unreferenced), so we compact afterwards to actually
 * shrink the attribute buffers and report a meaningful vertex-count diff.
 */
import * as THREE from 'three';
import { MeshoptSimplifier } from 'meshoptimizer';

const LOG_PREFIX = '[simplifyGeometry]';

const DEFAULT_TARGET_RATIO = 0.5; // keep ~50% of triangles
const DEFAULT_TARGET_ERROR = 0.01; // normalized to mesh extents

function isSimplifyEnabled() {
  const env = import.meta.env.VITE_MESH_SIMPLIFY_ENABLED;
  if (env !== undefined) return env === 'true' || env === true;
  return true;
}

function getTargetRatio() {
  const env = import.meta.env.VITE_MESH_SIMPLIFY_RATIO;
  if (env !== undefined) {
    const parsed = parseFloat(env);
    if (!Number.isNaN(parsed) && parsed > 0 && parsed < 1) return parsed;
  }
  return DEFAULT_TARGET_RATIO;
}

function getTargetError() {
  const env = import.meta.env.VITE_MESH_SIMPLIFY_ERROR;
  if (env !== undefined) {
    const parsed = parseFloat(env);
    if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
  }
  return DEFAULT_TARGET_ERROR;
}

/**
 * Rebuild a geometry keeping only the vertices referenced by `indices`,
 * remapping every existing attribute. Returns a fresh indexed BufferGeometry.
 */
function compactGeometry(geometry, indices) {
  const oldCount = geometry.attributes.position.count;
  const oldToNew = new Int32Array(oldCount).fill(-1);
  const newIndices = new Uint32Array(indices.length);

  let next = 0;
  for (let i = 0; i < indices.length; i++) {
    const v = indices[i];
    if (oldToNew[v] === -1) oldToNew[v] = next++;
    newIndices[i] = oldToNew[v];
  }
  const newCount = next;

  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(geometry.attributes)) {
    const attr = geometry.attributes[name];
    const itemSize = attr.itemSize;
    const ArrayType = attr.array.constructor;
    const newArr = new ArrayType(newCount * itemSize);
    for (let oldV = 0; oldV < oldCount; oldV++) {
      const nv = oldToNew[oldV];
      if (nv === -1) continue;
      for (let c = 0; c < itemSize; c++) {
        newArr[nv * itemSize + c] = attr.array[oldV * itemSize + c];
      }
    }
    out.setAttribute(name, new THREE.BufferAttribute(newArr, itemSize, attr.normalized));
  }
  out.setIndex(new THREE.BufferAttribute(newIndices, 1));
  return out;
}

/**
 * Decimate an indexed geometry with meshoptimizer.
 * @param {THREE.BufferGeometry} geometry - Indexed geometry to simplify.
 * @param {Object} [options]
 * @param {number} [options.targetRatio] - Fraction of triangles to keep (0–1).
 * @param {number} [options.targetError] - Max normalized error allowed.
 * @param {string} [options.source] - Label for logs.
 * @returns {Promise<{ geometry: THREE.BufferGeometry, stats: Object }>}
 *   The (possibly new) geometry plus a before/after stats object. On skip or
 *   failure the original geometry is returned unchanged with stats.applied=false.
 */
export async function simplifyGeometry(geometry, options = {}) {
  const source = options.source || 'Unknown';
  const beforeVertices = geometry.attributes.position.count;
  const beforeTriangles = geometry.index ? geometry.index.count / 3 : beforeVertices / 3;

  const stats = {
    applied: false,
    before: { vertices: beforeVertices, triangles: Math.floor(beforeTriangles) },
    after: { vertices: beforeVertices, triangles: Math.floor(beforeTriangles) },
    error: 0,
  };

  if (!isSimplifyEnabled()) {
    console.log(`${LOG_PREFIX} Disabled (VITE_MESH_SIMPLIFY_ENABLED=false) [${source}]`);
    return { geometry, stats };
  }
  if (!geometry.index) {
    console.warn(`${LOG_PREFIX} Skipped: geometry is not indexed [${source}]`);
    return { geometry, stats };
  }

  await MeshoptSimplifier.ready;
  if (!MeshoptSimplifier.supported) {
    console.warn(`${LOG_PREFIX} Skipped: MeshoptSimplifier not supported in this runtime [${source}]`);
    return { geometry, stats };
  }

  const posArray = geometry.attributes.position.array;
  const positions = posArray instanceof Float32Array ? posArray : new Float32Array(posArray);
  const srcIndex = geometry.index.array;
  const indices = srcIndex instanceof Uint32Array ? srcIndex : new Uint32Array(srcIndex);

  const targetRatio = options.targetRatio ?? getTargetRatio();
  const targetError = options.targetError ?? getTargetError();
  // target_index_count must be a multiple of 3 and within [0, indices.length].
  const targetIndexCount = Math.max(3, Math.floor((indices.length * targetRatio) / 3) * 3);

  let newIndices;
  let resultError = 0;
  try {
    [newIndices, resultError] = MeshoptSimplifier.simplify(
      indices,
      positions,
      3,
      targetIndexCount,
      targetError,
      ['LockBorder'], // preserve open boundaries (scans are rarely watertight)
    );
  } catch (err) {
    console.warn(`${LOG_PREFIX} simplify() failed, keeping original [${source}]:`, err);
    return { geometry, stats };
  }

  // If nothing was removed, avoid a pointless rebuild.
  if (newIndices.length >= indices.length) {
    console.log(`${LOG_PREFIX} No reduction possible at error ${targetError} [${source}]`);
    return { geometry, stats };
  }

  const simplified = compactGeometry(geometry, newIndices);
  simplified.computeVertexNormals();
  geometry.dispose();

  const afterVertices = simplified.attributes.position.count;
  const afterTriangles = simplified.index.count / 3;

  stats.applied = true;
  stats.after = { vertices: afterVertices, triangles: Math.floor(afterTriangles) };
  stats.error = resultError;

  const vReduction = ((1 - afterVertices / beforeVertices) * 100).toFixed(1);
  const tReduction = ((1 - afterTriangles / beforeTriangles) * 100).toFixed(1);
  console.log(`\n🪶 MESH SIMPLIFY [${source}] (target ${(targetRatio * 100).toFixed(0)}% tris, err ${targetError}):`);
  console.log(`├─ Vertices:  ${beforeVertices.toLocaleString()} → ${afterVertices.toLocaleString()} (-${vReduction}%)`);
  console.log(`├─ Triangles: ${Math.floor(beforeTriangles).toLocaleString()} → ${Math.floor(afterTriangles).toLocaleString()} (-${tReduction}%)`);
  console.log(`└─ Result error (normalized): ${resultError.toExponential(3)}\n`);

  return { geometry: simplified, stats };
}
